import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap, catchError, EMPTY } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthenticateRequest, AuthenticateResponse, UserRole } from '../models';

const TOKEN_KEY = 'token';
const ROLE_KEY = 'role';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly _token = signal<string | null>(localStorage.getItem(TOKEN_KEY));
  private readonly _role = signal<UserRole>(
    (Number(localStorage.getItem(ROLE_KEY)) as UserRole) || UserRole.None
  );

  readonly isAuthenticated = computed(() => !!this._token());
  readonly currentRole = computed(() => this._role());
  readonly token = computed(() => this._token());

  isRefreshing = false;
  private readonly _refreshSubject = new BehaviorSubject<boolean>(false);
  readonly refresh$ = this._refreshSubject.asObservable();

  startRefresh(): void {
    this.isRefreshing = true;
    this._refreshSubject.next(false);
  }

  finishRefresh(success: boolean): void {
    this.isRefreshing = false;
    this._refreshSubject.next(success);
  }

  login(email: string, password: string): Observable<AuthenticateResponse> {
    const body: AuthenticateRequest = { email, password };
    return this.http
      .post<AuthenticateResponse>(`${environment.apiBaseUrl}/User/authenticate`, body, {
        withCredentials: true,
      })
      .pipe(
        tap((response) => {
          localStorage.setItem(TOKEN_KEY, response.jwtToken);
          localStorage.setItem(ROLE_KEY, String(response.role));
          this._token.set(response.jwtToken);
          this._role.set(response.role);
        })
      );
  }

  refreshToken(): Observable<AuthenticateResponse> {
    return this.http
      .get<AuthenticateResponse>(`${environment.apiBaseUrl}/User/refresh-token`, {
        withCredentials: true,
      })
      .pipe(
        tap((response) => {
          localStorage.setItem(TOKEN_KEY, response.jwtToken);
          localStorage.setItem(ROLE_KEY, String(response.role));
          this._token.set(response.jwtToken);
          this._role.set(response.role);
        })
      );
  }

  revokeToken(): void {
    this.http
      .post(`${environment.apiBaseUrl}/User/revoke-token`, {}, { withCredentials: true })
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  logout(): void {
    this.revokeToken();
    this.isRefreshing = false;
    this._refreshSubject.next(false);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    this._token.set(null);
    this._role.set(UserRole.None);
    this.router.navigate(['/login']);
  }
}
